import React, { useState } from "react";
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import InsightsIcon from "@mui/icons-material/Insights";

const Sidebar = ({ role, onNavigate, onLogout }) => {
  const [collapsed, setCollapsed] = useState(false);
  const isMobile = useMediaQuery("(max-width:600px)");

  const COLLAPSED_WIDTH = 56;
  const EXPANDED_WIDTH = 170;
  const ICON_BOX_SIZE = 48;

  const tutorNavItems = [
    { label: "Dashboard", icon: <DashboardIcon />, key: "dashboard" },
    { label: "Students", icon: <PeopleIcon />, key: "students" },
    { label: "Assignments", icon: <AssignmentIcon />, key: "assignments" },
  ];

  const studentNavItems = [
    { label: "Dashboard", icon: <AssignmentIcon />, key: "dashboard" },
    { label: "Analytics", icon: <InsightsIcon />, key: "analytics" },
  ];

  const navItems = role === "tutor" ? tutorNavItems : studentNavItems;
  
  return (
    <Box
      sx={{
        width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        minWidth: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        maxWidth: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        height: "100vh",
        flexShrink: 0,
        background: "rgba(255, 255, 255, 0.6)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        transition: "width 0.3s ease",
        display: "flex",
        flexDirection: "column",
        color: "#000",
        zIndex: 1000,
        boxShadow: "2px 0 8px rgba(0,0,0,0.05)",
      }}
    >
      {/* Toggle */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 3 }}>
        <IconButton
          onClick={() => setCollapsed(!collapsed)}
          sx={{ color: "#000" }}
        >
          <MenuIcon />
        </IconButton>
      </Box>

      {/* Main Nav */}
      <List sx={{ px: 0 }}>
        {navItems.map((item) => (
          <Tooltip
            key={item.key}
            title={collapsed ? item.label : ""}
            placement="right"
          >
            <ListItem
              component="button"
              onClick={() => onNavigate(item.key)}
              sx={{
                borderRadius: 2,
                my: 0.5,
                px: 0,
                py: 0.75,
                alignItems: "center",
                height: ICON_BOX_SIZE + 4,
                backgroundColor: "transparent",
                border: "none",
                outline: "none",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.08)",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: "#000",
                  minWidth: COLLAPSED_WIDTH,
                  width: COLLAPSED_WIDTH,
                  justifyContent: "center",
                  display: "flex",
                  alignItems: "center",
                  height: ICON_BOX_SIZE,
                }}
              >
                {item.icon}
              </ListItemIcon>

              <Box
                sx={{
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  opacity: collapsed ? 0 : 1,
                  maxWidth: collapsed ? 0 : 120,
                  transition: collapsed
                    ? "opacity 0.15s ease, max-width 0.2s ease"
                    : "max-width 0.3s ease, opacity 0.2s ease 0.15s",
                }}
              >
                <ListItemText primary={item.label} />
              </Box>
            </ListItem>
          </Tooltip>
        ))}
      </List>

      <Box sx={{ flexGrow: 1 }} />

      {/* Logout */}
      <List
        sx={{
          px: 0,
          mb: isMobile
            ? `calc(env(safe-area-inset-bottom, 16px) + 12px)`
            : 2,
        }}
      >
        <Tooltip title={collapsed ? "Log Out" : ""} placement="right">
          <ListItem
            component="button"
            onClick={onLogout}
            sx={{
              borderRadius: 2,
              px: 0,
              py: 0.75,
              alignItems: "center",
              height: ICON_BOX_SIZE + 4,
              backgroundColor: "transparent",
              border: "none",
              outline: "none",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.08)",
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: "#000",
                minWidth: COLLAPSED_WIDTH,
                width: COLLAPSED_WIDTH,
                justifyContent: "center",
                display: "flex",
                alignItems: "center",
                height: ICON_BOX_SIZE + 4,
              }}
            >
              <LogoutIcon />
            </ListItemIcon>

            <Box
              sx={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                opacity: collapsed ? 0 : 1,
                maxWidth: collapsed ? 0 : 120,
                pointerEvents: collapsed ? "none" : "auto",
                transition: collapsed
                  ? "opacity 0.15s ease, max-width 0.2s ease"
                  : "max-width 0.3s ease, opacity 0.2s ease 0.15s",
              }}
            >
              <ListItemText
                primary="Log Out"
                primaryTypographyProps={{
                  noWrap: true,
                  sx: { whiteSpace: "nowrap" },
                }}
              />
            </Box>
          </ListItem>
        </Tooltip>
      </List>
    </Box>
  );
};

export default Sidebar;
